import { Eye, Handshake, Compass } from "lucide-react";
import { Reveal, Overline, GoldDiamond } from "@/components/Reveal";
import { scrollToId, FIRM } from "@/lib/site";
import { TEAM } from "@/lib/team";
import { useLang } from "@/lib/lang";

const VALUES_ICONS = [Eye, Handshake, Compass];

export default function About() {
  const { t } = useLang();
  return (
    <section data-testid="about-section" id="studio" className="relative overflow-hidden bg-white py-24 lg:py-32">
      <div className="mx-auto grid max-w-[1400px] grid-cols-1 items-start gap-14 px-6 lg:grid-cols-2 lg:gap-20 lg:px-12">
        <div className="relative">
          <Reveal>
            <div className="relative">
              <div className="absolute -left-4 -top-4 h-full w-full border border-gold" aria-hidden="true" />
              <div data-testid="about-image" className="relative overflow-hidden">
                <img
                  src="https://customer-assets-eiarnc6j.emergentagent.net/job_legal-studio-14/artifacts/dnhvuw3s_image_20260930_102357.jpg"
                  alt="Il salone dello Studio Legale Legalia a Manduria"
                  className="aspect-[4/3] w-full object-cover transition-transform duration-[1.6s] ease-out hover:scale-105"
                  loading="lazy"
                />
              </div>
              <div
                data-testid="about-city-badge"
                className="absolute -bottom-6 right-6 flex h-28 w-28 flex-col items-center justify-center bg-navy text-center shadow-[0_20px_50px_-20px_rgba(11,29,58,0.6)] lg:-right-8"
              >
                <span className="font-serif text-2xl font-semibold text-gold">{FIRM.city}</span>
                <span className="mt-1 font-mono text-[9px] uppercase tracking-[0.2em] text-alabaster/70">(TA) · Puglia</span>
              </div>
            </div>
          </Reveal>
        </div>

        <div className="pt-4">
          <Reveal>
            <Overline>{t.studio.overline}</Overline>
            <h2 className="mt-4 font-serif text-3xl font-medium leading-tight tracking-tight text-navy sm:text-4xl lg:text-5xl">
              {t.studio.title}
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-8 text-base leading-relaxed text-muted-ink">{t.studio.p1}</p>
            <p className="mt-5 text-base leading-relaxed text-muted-ink">{t.studio.p2}</p>
          </Reveal>

          <div className="mt-12 divide-y divide-hairline border-y border-hairline">
            {t.studio.values.map((v, i) => {
              const Icon = VALUES_ICONS[i];
              return (
                <Reveal key={v.title} delay={0.08 * i}>
                  <div className="group flex items-start gap-5 py-6 transition-colors duration-500">
                    <span className="mt-1 flex h-10 w-10 shrink-0 items-center justify-center border border-hairline text-gold-dark transition-colors duration-500 group-hover:border-gold group-hover:bg-gold group-hover:text-navy">
                      <Icon size={17} strokeWidth={1.25} />
                    </span>
                    <div>
                      <h3 className="flex items-center gap-3 font-serif text-xl font-semibold text-navy">
                        {v.title}
                        <GoldDiamond className="opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                      </h3>
                      <p className="mt-1.5 text-sm leading-relaxed text-muted-ink">{v.desc}</p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </div>

      <div className="mx-auto mt-20 max-w-[1400px] px-6 lg:px-12">
        <Reveal>
          <div className="flex items-center gap-6">
            <p className="shrink-0 font-mono text-[11px] uppercase tracking-[0.28em] text-gold-dark">{t.studio.stripLabel}</p>
            <span className="h-px flex-1 bg-hairline" aria-hidden="true"></span>
          </div>
        </Reveal>
        <div className="mt-8 grid max-w-3xl grid-cols-3 gap-4 lg:gap-6">
          {TEAM.filter((m) => m.role === "Socio").map((m, i) => (
            <Reveal key={m.id} delay={0.05 * i}>
              <button
                data-testid={`about-strip-${m.id}`}
                onClick={() => scrollToId("team")}
                className="group block w-full text-left"
                aria-label={`Vai al profilo di ${m.name}`}
              >
                <div className="overflow-hidden bg-navy">
                  <img
                    src={m.img}
                    alt={m.name}
                    loading="lazy"
                    className="aspect-square w-full object-cover object-top grayscale transition-all duration-700 ease-out group-hover:scale-105 group-hover:grayscale-0"
                  />
                </div>
                <p className="mt-2 truncate font-serif text-sm font-semibold text-navy">{m.name}</p>
                <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-muted-ink">{t.team.roles[m.role] || m.role}</p>
              </button>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
